import React, { useEffect, useState } from 'react';
import { isEmpty, isFunction } from 'lodash';

import AlertDialog from '../../shared/components/AlertDialog';
import Button from '@mui/material/Button';
import ChangeEmailDialog from '../../shared/components/ChangeEmailDialog';
import ChangePasswordDialog from '../../shared/components/ChangePasswordDialog';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import InlineTextEdit from '../../shared/components/InlineTextEdit';
import InlineTextEditCallback from '../../shared/models/InlineTextEditCallback';
import PersonIcon from '@mui/icons-material/Person';
import { Theme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import UpdateUserNamesRequest from '../../membership/models/UpdateUserNamesRequest';
import User from '../../membership/models/User';
import UserAvatar from '../../membership/components/UserAvatar';
import UserProfileEditor from '../../shared/components/UserProfileEditor';
import { avatarStyles } from '../../shared/hooks/useAvatarStyles';
import { styled } from '@mui/system';
import { useBlocker } from 'react-router-dom';
import useExternalUser from '../../membership/hooks/useExternalUser';
import useMediaQuery from '@mui/material/useMediaQuery';
import useSingletonUser from '../../shared/hooks/useSingletonUser';
import useSiteSettings from '../../siteadmin/hooks/useSiteSettings';
import { useUnlink } from '../../user/hooks/useUnlink';
import useUserEdit from '../../membership/hooks/useUserEdit';
import useUserProfile from '../../shared/hooks/useUserProfile';

const StyledToggle = styled(ToggleButton)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}));

interface UserAccountProps {
  forAdmin?: boolean; //* setting true allows site admins to edit users' details
  user?: User;
  handleProfileChanges?: (anyChanges: boolean) => void;
  onClose?: () => void;
}
const UserAccount = (props: UserAccountProps) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );
  const spacing = isSmallScreen ? 2 : 3;

  const [showChangeEmailDialog, setShowChangeEmailDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] =
    useState(false);

  const [confirmUnverifyEmail, setConfirmUnverifyEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    setupContext,
    verifyOrUnverifyEmailManually,
    updating,
    updatingError,
    updateUserNamesData
  } = useUserEdit();

  //Get loggedin user from singleton user hook
  const { user: loggedInUser, setUser } = useSingletonUser();

  //Set currentUser as selected user if site admin/group admin edit user details otherwise loggedin user
  const [currentUser, setCurrentUser] = useState<User>(
    props.forAdmin ? props.user : loggedInUser
  );
  const {
    userkey,
    username,
    firstname,
    lastname,
    email,
    verifiedemail,
    parentkey
  } = currentUser || {};
  const { showUsername } = useExternalUser(userkey, currentUser);

  const isLinkedUser = !!parentkey;
  const [verify, setVerify] = useState<boolean>(!!verifiedemail);
  const [unlink, setUnlink] = useState<boolean>(false);

  //Hide verify button for linked accounts(accounts don't have email address)
  const showVerify = !!email;
  const toggleLabel: string = verify ? 'Unverify' : 'Verify';

  const {
    handleProfileChange,
    saveFieldValuesAsync,
    loading,
    loadError,
    saving,
    saveError,
    profileFields,
    avatarProfileField,
    userProfileFields,
    profileFieldInputs,
    allRequiredFilledIn,
    anyChanges
  } = useUserProfile(userkey);

  const {
    removeLinkedAccount,
    removeLinkedAccountData,
    removeLinkedAccountError,
    removeLoading
  } = useUnlink();

  useEffect(() => {
    if (updateUserNamesData) {
      if (!props.forAdmin) {
        setUser(updateUserNamesData);
        setCurrentUser(updateUserNamesData);
      }
    }
  }, [updateUserNamesData]);

  //Blocks navigation away from this page when there is any unsaved profile changes.
  let blocker = useBlocker(anyChanges);

  useEffect(() => {
    //Call this to trigger any unsaved profile changes when using UserProfile dialog
    if (isFunction(props.handleProfileChanges)) {
      props.handleProfileChanges(anyChanges);
    }
  }, [anyChanges]);

  //Set error message when there is conflict in verifying email address.
  //PublicAPI returns status code 409 conflict when the email address is already taken by another account.
  useEffect(() => {
    if (updatingError && updatingError.graphQLErrors.length) {
      if (updatingError.graphQLErrors[0].extensions.statusCode === 409) {
        setErrorMessage(updatingError.message);
      }
    }
    if (removeLinkedAccountError) {
      setErrorMessage(removeLinkedAccountError.message);
    }
  }, [updatingError, removeLinkedAccountError]);

  const { siteSettingsData } = useSiteSettings();

  const canSubmit = () => !saving && allRequiredFilledIn && anyChanges;

  const submitFirstName = (value: string, cb: InlineTextEditCallback<User>) => {
    const request: UpdateUserNamesRequest = {
      userkey,
      firstname: value
    };
    handleUpdateUserNames(request, cb);
  };

  const submitLastName = (value: string, cb: InlineTextEditCallback<User>) => {
    const request: UpdateUserNamesRequest = {
      userkey,
      lastname: value
    };
    handleUpdateUserNames(request, cb);
  };

  const handleUpdateUserNames = (
    request: UpdateUserNamesRequest,
    callback?: InlineTextEditCallback<User>
  ) => {
    setupContext(request, callback);
  };

  const handleUnverifyEmail = async () => {
    await verifyOrUnverifyEmailManually(userkey);
  };

  const handleVerifyEmail = async () => {
    await verifyOrUnverifyEmailManually(userkey, email);
  };

  const openChangeEmailDialog = () => {
    setShowChangeEmailDialog(true);
  };

  const closeChangeEmailDialog = (email?: string) => {
    setShowChangeEmailDialog(false);
    if (email) {
      setCurrentUser({ ...currentUser, email });
    }
  };

  const openChangePasswordDialog = () => {
    setShowChangePasswordDialog(true);
  };

  const closeChangePasswordDialog = () => {
    setShowChangePasswordDialog(false);
  };

  const handleSubmit = async () => {
    await saveFieldValuesAsync(userkey, profileFieldInputs);
  };

  const handleVerifyToggle = () => {
    if (!verify) {
      setVerify(!verify);
      handleVerifyEmail();
    } else {
      setConfirmUnverifyEmail(true);
    }
  };

  const doUnverifyEmail = () => {
    setVerify(!verify);
    handleUnverifyEmail();
  };

  const handleUnlink = async () => {
    await removeLinkedAccount(userkey);
  };

  /** Site admin can unlink an account without entering email address.
   * Child user must provide a valid email address to proceed with unlinking.
   */
  const handleUnlinkClick = async () => {
    if (props.forAdmin) {
      await handleUnlink();
      setCurrentUser({ ...currentUser, parentkey: null });
    } else {
      setUnlink(true);
      openChangeEmailDialog();
    }
  };

  return (
    <>
      <Grid container direction="column" spacing={2}>
        <Grid>
          <Grid container spacing={spacing} wrap="nowrap" alignItems="center">
            <Grid>
              <UserAvatar
                userkey={userkey}
                required={avatarProfileField?.required}
                edit={!!avatarProfileField}
                sx={avatarStyles}
              />
            </Grid>
            <Grid>
              <Grid container wrap="nowrap" direction="column" spacing={1}>
                <Grid>
                  <InlineTextEdit
                    value={firstname}
                    errorMessage="Updating first name has failed."
                    onSubmit={submitFirstName}
                  >
                    <Typography
                      variant={isSmallScreen ? 'h5' : 'h4'}
                      className="edit-userdetails"
                    >
                      {firstname}
                    </Typography>
                  </InlineTextEdit>
                </Grid>
                <Grid>
                  <InlineTextEdit
                    value={lastname}
                    errorMessage="Updating last name has failed."
                    onSubmit={submitLastName}
                  >
                    <Typography
                      variant={isSmallScreen ? 'h5' : 'h4'}
                      className="edit-userdetails"
                    >
                      {lastname}
                    </Typography>
                  </InlineTextEdit>
                </Grid>
                {showUsername && (
                  <Grid>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid>
                        <PersonIcon fontSize="small" />
                      </Grid>
                      <Grid xs>
                        <Typography
                          variant="body1"
                          className={'dashboard-userdetails'}
                        >
                          {showUsername}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {isLinkedUser ? (
                  <Grid>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleUnlinkClick}
                    >
                      {removeLoading && props.forAdmin
                        ? 'Unlinking...'
                        : 'Unlink from parent'}
                    </Button>
                  </Grid>
                ) : (
                  // Hide email editing for linked account
                  <Grid>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid>
                        <EmailIcon fontSize="small" />
                      </Grid>
                      <Grid xs onClick={openChangeEmailDialog}>
                        <Typography
                          variant="body1"
                          className={'edit-userdetails'}
                          sx={
                            props.forAdmin &&
                            siteSettingsData?.siteSettings?.flags
                              .requireEmailVerification &&
                            email &&
                            isEmpty(verifiedemail)
                              ? { fontStyle: 'italic' }
                              : undefined
                          }
                        >
                          {email || 'Not set'}
                        </Typography>
                      </Grid>
                      <Grid>
                        <IconButton
                          size="small"
                          onClick={openChangeEmailDialog}
                        >
                          <EditIcon />
                        </IconButton>
                      </Grid>
                      {props.forAdmin &&
                        siteSettingsData?.siteSettings?.flags
                          .requireEmailVerification &&
                        showVerify && (
                          <Grid>
                            {updating ? (
                              <CustomLoadingProgress size={20} />
                            ) : (
                              <StyledToggle
                                value={toggleLabel}
                                size="small"
                                onChange={handleVerifyToggle}
                              >
                                {toggleLabel}
                              </StyledToggle>
                            )}
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                )}
                <Grid>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={openChangePasswordDialog}
                  >
                    Change password
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        {!isEmpty(profileFields) && (
          <>
            <Grid>
              <Grid container spacing={1} alignItems="center">
                <Grid>
                  <Typography variant="body1">
                    Additional Information
                  </Typography>
                </Grid>
              </Grid>
              <UserProfileEditor
                userkey={userkey}
                hideSubFields={false}
                indicateErrorOnLoad
                loading={loading}
                error={loadError}
                profileFields={profileFields}
                userProfileFields={userProfileFields}
                values={profileFieldInputs}
                onChange={handleProfileChange}
              />
            </Grid>
            <Grid container justifyContent="center">
              <Button
                variant="contained"
                size="small"
                color="primary"
                disabled={!canSubmit()}
                onClick={handleSubmit}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      {showChangeEmailDialog && (
        <ChangeEmailDialog
          open={showChangeEmailDialog}
          userkey={userkey}
          username={username}
          firstname={firstname}
          lastname={lastname}
          email={email}
          forAdmin={props.forAdmin}
          unlink={unlink}
          unlinkSuccess={removeLinkedAccountData?.removeLinkedAccount}
          onClose={closeChangeEmailDialog}
          onUnlink={handleUnlink}
        />
      )}
      {showChangePasswordDialog && (
        <ChangePasswordDialog
          open={showChangePasswordDialog}
          userkey={userkey}
          username={username}
          checkCurrentPassword={!props.forAdmin}
          onClose={closeChangePasswordDialog}
        />
      )}
      {confirmUnverifyEmail && (
        <ConfirmationDialog
          open={true}
          title="Unverify Email Address"
          message={`Are you sure you want to unverify ${username}'s email address?`}
          onClose={async (agreed: boolean) => {
            setConfirmUnverifyEmail(false);
            if (agreed) {
              doUnverifyEmail();
            }
          }}
        />
      )}
      {errorMessage && (
        <AlertDialog
          title="Update error"
          message={`${email} has already been verified by another account.`}
          onClose={props.onClose}
        />
      )}
      {blocker.state === 'blocked' && (
        <ConfirmationDialog
          open={true}
          title={`Are you sure you want to continue?`}
          message="Some profile fields have been updated. If you continue, your changes will be lost."
          onClose={async (agreed: boolean) => {
            if (agreed) {
              blocker.proceed();
            } else {
              blocker.reset();
            }
          }}
        />
      )}
    </>
  );
};

export default UserAccount;
